/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import AvatarGroup from '@atlaskit/avatar-group';
import { cssMap } from '@atlaskit/css';
import { ProfileCardTrigger, ProfileClient } from '@atlaskit/profilecard';
import { token } from '@atlaskit/tokens';
import { useProfileTrigger } from '@atlassian/rovo-platform-ui-components/profile-trigger';

import { formatFirstNames } from '../../utils/format-first-names';

export interface ProfilePerson {
	/** Full display name of the person. */
	name: string;
	/** Atlassian Account ID. */
	aaid: string;
	/** URL for the user's avatar image. */
	avatarUrl: string;
}

const getCloudId = (): string | undefined => {
	if (typeof document === 'undefined') {
		return undefined;
	}
	return document.querySelector<HTMLMetaElement>('meta[name="ajs-cloud-id"]')?.content;
};

let profileClientInstance: ProfileClient | undefined;
const getProfileClient = (): ProfileClient => {
	if (!profileClientInstance) {
		profileClientInstance = new ProfileClient({
			cacheMaxAge: 86400000,
			cacheSize: 1000,
			url: '/gateway/api/directory/graphql',
		});
	}
	return profileClientInstance;
};

const styles = cssMap({
	container: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
		minWidth: '0px',
		maxWidth: '100%',
		overflow: 'hidden',
	},
	names: {
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		minWidth: '0px',
		maxWidth: '8.25rem',
		flexShrink: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

interface ProfileAvatarGroupProps {
	/** List of people to display as avatars. */
	people: ProfilePerson[];
	/** Test ID for the AvatarGroup. */
	testId?: string;
	/**
	 * Maximum number of avatars to display before showing a "+N" indicator.
	 * When the number of people exceeds this value, the first-name text is hidden
	 * (only the avatar stack + "+N" indicator is rendered).
	 */
	maxCount?: number;
	/**
	 * When true, renders names to the LEFT of the avatar stack (fullscreen layout).
	 * When false (default), renders names to the RIGHT (sidebar layout).
	 */
	namesFirst?: boolean;
}

/**
 * AvatarGroup with profile card popups on hover.
 *
 * Uses the same pattern as Townsquare's DirectoryManageViewDialog:
 * - AvatarGroup's `overrides.Avatar.render` wraps each avatar in `ProfileCardTrigger`
 *   without breaking the group layout
 * - `isTooltipDisabled` suppresses the default tooltip (no AAID shown)
 * - Profile data is resolved via `/gateway/api/directory/graphql`
 * - cloudId is read from `meta[name="ajs-cloud-id"]` (standard Atlassian product pages)
 *
 * Avatar image URLs are provided by the backend via the `avatarUrl` field on each person.
 */
export const ProfileAvatarGroup = ({
	people,
	testId = 'insight-avatar-group',
	maxCount = 3,
	namesFirst = false,
}: ProfileAvatarGroupProps): JSX.Element => {
	const cloudId = getCloudId();
	// Injected by a provider outside the ai-mate cluster (keeps this package free of a
	// direct @atlassian/user-profile-card dependency). Falls back to legacy when absent.
	const renderProfileTrigger = useProfileTrigger();

	// Only show the first-name text when the number of people fits within the
	// max avatar count. When there are more, AvatarGroup uses the last visible
	// slot as the overflow trigger, which stands in for the remaining people.
	const showNames = people.length > 0 && people.length <= maxCount;
	const namesText = showNames ? formatFirstNames(people.map((p) => p.name)) : '';

	const avatarGroup = (
		<AvatarGroup
			testId={testId}
			appearance="stack"
			size="small"
			maxCount={maxCount}
			isTooltipDisabled
			data={people.map((person) => ({
				name: person.name,
				src: person.avatarUrl,
				key: person.aaid,
			}))}
			overrides={{
				Avatar: {
					render: (Component, props, index) => {
						const aaid = props.key;
						const avatarItem = (
							<Component {...props} key={`profile-avatar-${props.key ?? index}`} />
						);
						if (typeof aaid === 'string' && cloudId) {
							if (renderProfileTrigger) {
								return renderProfileTrigger({
									userId: aaid,
									cloudId,
									children: avatarItem,
								});
							}
							return (
								<ProfileCardTrigger
									key={`profile-avatar-${props.key ?? index}`}
									resourceClient={getProfileClient()}
									cloudId={cloudId}
									userId={aaid}
								>
									{avatarItem}
								</ProfileCardTrigger>
							);
						}
						return avatarItem;
					},
				},
			}}
		/>
	);

	const namesElement = namesText ? (
		<span css={styles.names} data-testid={`${testId}-names`}>
			{namesText}
		</span>
	) : null;

	return (
		<span css={styles.container}>
			{namesFirst && namesElement}
			{avatarGroup}
			{!namesFirst && namesElement}
		</span>
	);
};
